import { createFileRoute, Link } from "@tanstack/react-router";
import { ServicePage } from "@/components/ServicePage";
import {
  Target,
  MousePointerClick,
  FileText,
  BarChart3,
  Phone,
  RefreshCw,
  Wrench,
  Truck,
  Home as HomeIcon,
  Hammer,
  Trees,
  Zap,
  ArrowRight,
} from "lucide-react";
import { SERVICE_PRICING } from "@/lib/pricingData";
import { CASE_STUDIES } from "@/lib/caseStudiesData";
import { CLIENT_REVIEWS } from "@/lib/clientReviews";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";

const pricing = SERVICE_PRICING.find((s) => s.slug === "ppc");
const OG_IMAGE = `https://coloradowebimpressions.com${ogImageFor({
  service: "ppc",
  city: "colorado-springs",
})}`;

const FEATURED_CASE = CASE_STUDIES.find((c) => c.slug === "hvac-colorado-springs")
  ?? CASE_STUDIES[0];

const SPRINGS_REVIEWS = CLIENT_REVIEWS.filter((r) =>
  /Colorado Springs|Monument/i.test(r.location ?? ""),
).slice(0, 4);

export const Route = createFileRoute("/colorado-springs-ppc")({
  headers: () => ({
    "Cache-Control": "public, max-age=0, s-maxage=604800, stale-while-revalidate=2592000",
    "CDN-Cache-Control": "public, s-maxage=604800, stale-while-revalidate=2592000",
  }),
  head: () => ({
    meta: [
      {
        title:
          "Colorado Springs Google Ads — No Ad-Spend Markup (2026)",
      },
      {
        name: "description",
        content:
          "Owner-led Colorado Springs Google Ads management. Zero markup on ad spend, dedicated landing pages, full call tracking. Month-to-month since 2006.",
      },
      {
        property: "og:title",
        content: "Colorado Springs Google Ads — No Ad-Spend Markup (2026)",
      },
      {
        property: "og:description",
        content:
          "Owner-managed PPC for Springs businesses. Zero ad-spend markup, real call tracking, month-to-month.",
      },
      { property: "og:url", content: "https://coloradowebimpressions.com/colorado-springs-ppc" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: "Colorado Springs PPC — No Ad-Spend Markup",
        description:
          "Owner-led Google Ads with full call tracking and dedicated landing pages.",
      }),
    ],
    links: [
      {
        rel: "canonical",
        href: "https://coloradowebimpressions.com/colorado-springs-ppc",
      },
    ],
  }),
  component: () => (
    <ServicePage
      slug="ppc"
      serviceName="Google Ads / PPC"
      pricingRoute="/pricing"
      eyebrow="Colorado Springs PPC"
      title="Colorado Springs Google Ads that turn clicks into booked work"
      goldWord="booked work"
      description="Profitable, fully-tracked Google Ads campaigns for Colorado Springs businesses — built and managed by Chris from our Springs office, not a junior media buyer in another state."
      intro={{
        heading:
          "Most Colorado Springs PPC accounts leak money because nobody's watching the meter.",
        body: [
          "Springs Google Ads CPCs in HVAC, roofing, legal, and home services have nearly doubled in the last three years. Without disciplined match types, tight geo-targeting, and end-to-end conversion tracking, you'll burn budget on Pueblo, Castle Rock, and base-housing clicks that rarely convert. We rebuild your account around real Springs search behavior, install full call + form tracking, and ship dedicated landing pages so every dollar is accountable.",
          "We're headquartered in Colorado Springs and serve businesses across Colorado Springs, Castle Rock, Monument, Parker, Fountain, Pueblo, and the broader Front Range. Most engagements start in the Springs and expand into surrounding markets as the account compounds — but every campaign is geo-fenced precisely to where you actually do work.",
          "Sending Springs Google Ads traffic to your homepage is a guaranteed ROI killer. We build dedicated, fast-loading landing pages for each service and each major Springs neighborhood — and that single change typically doubles conversion rate.",
        ],
      }}
      benefits={[
        "Full Google Ads account rebuild around Colorado Springs intent",
        "Local Services Ads (LSA) setup and management — green checkmark visibility",
        "Dedicated landing pages per service and Springs neighborhood",
        "End-to-end call + form + offline-conversion tracking",
        "Geo-targeting tuned to El Paso County (and broader Front Range when you serve it)",
        "Owner-led — direct line to Chris, not a layered account team",
      ]}
      features={[
        {
          icon: Target,
          title: "Springs Campaign Strategy",
          desc: "Built around your real Springs service area, ticket size, and capacity — not a generic template.",
        },
        {
          icon: MousePointerClick,
          title: "Local Services Ads",
          desc: "Get featured at the very top of Google with the trusted green checkmark — most Springs categories are wide open.",
        },
        {
          icon: FileText,
          title: "Dedicated Landing Pages",
          desc: "Fast, mobile-first pages per service and per Springs neighborhood — Briargate, Falcon, Monument, Black Forest.",
        },
        {
          icon: Phone,
          title: "Full Call Tracking",
          desc: "Every Springs Google Ads call tracked, recorded, and tied back to the exact campaign and keyword.",
        },
        {
          icon: RefreshCw,
          title: "Weekly Optimization",
          desc: "Negatives, bid adjustments, ad copy testing, and landing-page tweaks every single week.",
        },
        {
          icon: BarChart3,
          title: "Plain-English ROAS",
          desc: "A 60-second monthly report the owner can read — not a 30-page agency PDF nobody opens.",
        },
      ]}
      frameworkHeading="The Colorado Springs Lead-Engine PPC Framework"
      frameworkIntro="A 5-step framework that turns Springs Google Ads spend into booked jobs — refined across HVAC, plumbing, roofing, legal, and dental accounts on the Front Range."
      framework={[
        {
          number: "01",
          title: "Offer & Audience",
          desc: "Define highest-margin services, the Springs neighborhoods and El Paso County ZIPs to target, and the offers your competitors aren't running.",
        },
        {
          number: "02",
          title: "Campaign Architecture",
          desc: "Search, Local Services, and Performance Max campaigns structured for clean attribution by service and Springs neighborhood.",
        },
        {
          number: "03",
          title: "Landing Page Build",
          desc: "Conversion-focused pages with click-to-call, Springs-specific trust signals, and forms tuned to local intent.",
        },
        {
          number: "04",
          title: "Tracking & Attribution",
          desc: "Call tracking, form attribution, and CRM integration so you know exactly what each Springs lead cost.",
        },
        {
          number: "05",
          title: "Weekly Optimization",
          desc: "Negative keywords, bid adjustments, ad copy testing, and landing page tweaks every week — not a quarterly review.",
        },
      ]}
      pricing={pricing}
      industries={[
        {
          icon: Wrench,
          name: "HVAC PPC",
          desc: "Emergency AC & furnace campaigns timed to Springs weather spikes and PCS season.",
        },
        {
          icon: Truck,
          name: "Plumbing PPC",
          desc: "24/7 emergency-service ads with click-to-call landing pages across Briargate, Falcon, and Monument.",
        },
        {
          icon: HomeIcon,
          name: "Roofing PPC",
          desc: "Hail-storm season campaigns for Springs and Front Range roofers — the ROI peak of the year.",
        },
        {
          icon: Hammer,
          name: "Remodelers",
          desc: "High-ticket remodel & build campaigns with qualifying form steps to filter tire-kickers.",
        },
        {
          icon: Trees,
          name: "Landscaping",
          desc: "Seasonal Google Ads for design, install, and maintenance services in the Pikes Peak Region.",
        },
        {
          icon: Zap,
          name: "Electrical",
          desc: "Local Services Ads + Search campaigns for emergency electricians across the Springs.",
        },
      ]}
      faqs={[
        {
          q: "Are you actually based in Colorado Springs?",
          a: "Yes — we're headquartered in Colorado Springs at 3210 E Woodmen Rd STE 110. You can drive over and meet Chris. He runs every PPC account personally — no junior media buyers, no offshore handoffs.",
        },
        {
          q: "How much should a Colorado Springs business spend on Google Ads?",
          a: "It depends on industry and capacity. HVAC and plumbing in Colorado Springs typically need $1,500–$3,000/month in ad spend to compete. Roofing and legal can run higher. We'll honestly tell you upfront if PPC isn't the right move for your stage of business.",
        },
        {
          q: "How much does Google Ads management cost?",
          a: "Paid ad management is included in our Expert Marketing program at $2,750/month — month-to-month, no long-term contract. That also includes multi-city SEO, Google Maps, AI search optimization, and social media. Ad spend not included — it goes straight from your card to the ad platform, and we never mark it up.",
        },
        {
          q: "Will you manage my existing Google Ads account or rebuild it?",
          a: "Most of the time we rebuild from scratch — match types, negatives, ad groups, conversion tracking, and landing pages. Inherited Springs accounts usually have years of accumulated waste that's faster to start over than to clean up.",
        },
        {
          q: "Do you also serve Castle Rock, Monument, Parker, and Pueblo?",
          a: "Yes. We're headquartered in Colorado Springs but most clients we work with also serve Monument, Tri-Lakes, Falcon, Fountain, Castle Rock, Parker, and sometimes Pueblo. We geo-target precisely to where you actually do work — not generic 'Colorado' targeting that wastes budget.",
        },
        {
          q: "How fast can I get leads from PPC?",
          a: "Campaigns typically go live in 7–10 days. Once live, leads usually start the same day, with cost-per-lead optimizing meaningfully over the first 30 days.",
        },
        {
          q: "Can I run PPC and SEO at the same time in Colorado Springs?",
          a: "Yes — and most successful Springs service businesses do exactly that. PPC delivers immediate flow while SEO and Map Pack rankings build. The two channels reinforce each other.",
        },
      ]}
      sectionsAfterPricing={<SpringsLocalProof />}
    />
  ),
});

function SpringsLocalProof() {
  return (
    <section className="bg-section-alt py-20 lg:py-28" aria-labelledby="springs-ppc-proof-heading">
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-block rounded-full border border-brand-gold-deep/30 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
            Real Colorado Springs PPC results
          </span>
          <h2 id="springs-ppc-proof-heading" className="mt-5 text-4xl font-extrabold text-foreground sm:text-5xl">
            What <span className="text-gradient-gold">Springs PPC</span> looks like in practice
          </h2>
        </div>

        <div className="mt-12 overflow-hidden rounded-2xl border border-brand-gold/40 bg-card p-8 shadow-card sm:p-10">
          <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-center">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
                Springs case study
              </div>
              <h3 className="mt-3 text-2xl font-extrabold text-foreground sm:text-3xl">
                {FEATURED_CASE.headline}
              </h3>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                {FEATURED_CASE.summary}
              </p>
              {FEATURED_CASE.quote && (
                <blockquote className="mt-5 border-l-4 border-brand-gold pl-4 text-base italic text-foreground/90">
                  "{FEATURED_CASE.quote.text}"
                  <footer className="mt-2 text-sm font-semibold not-italic text-muted-foreground">
                    — {FEATURED_CASE.quote.attribution}
                  </footer>
                </blockquote>
              )}
            </div>
            <div className="rounded-xl bg-gradient-gold p-6 text-center text-brand-black">
              <div className="text-xs font-bold uppercase tracking-[0.2em] opacity-80">
                Result
              </div>
              <div className="mt-2 text-3xl font-black sm:text-4xl">
                {FEATURED_CASE.heroResult}
              </div>
            </div>
          </div>
        </div>

        {SPRINGS_REVIEWS.length > 0 && (
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            {SPRINGS_REVIEWS.map((r) => (
              <figure key={r.author} className="rounded-2xl border border-border bg-card p-6 shadow-card">
                <blockquote className="text-sm leading-relaxed text-foreground/90">
                  "{r.body}"
                </blockquote>
                <figcaption className="mt-4 text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
                  {r.author}
                  {r.location ? ` — ${r.location}` : ""}
                </figcaption>
              </figure>
            ))}
          </div>
        )}

        <div className="mt-10 text-center">
          <Link
            to="/contact"
            className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
          >
            Get a free Springs PPC audit
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}
